import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { SidebarItemComponent } from '@modules/common/components/sidebar-item/sidebar-item.component';
import { SidebarItem } from '@modules/common/types/sidebar-item';
import { DragNDropModule } from '@modules/drag-n-drop/drag-n-drop.module';
import { PermissionsService } from '@frontendlib/common/features/permissions/services/permissions.service';
import { ClickDirective } from '@modules/utils/directives/click/click.directive';
import { type Actions, type Subjects, ViewSubjects } from '@sharedlib/common/permissions/types/Permissions';
import { Subject } from 'rxjs';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-settings-side-bar',
  templateUrl: './side-bar.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SidebarItemComponent, DragNDropModule, ClickDirective],
})
export class SettingsSideBarComponent implements OnInit {
  /**
   * The per-container "Shared" pages (Folders, Calendars, Projects, Notebooks, Groups) are
   * commented out rather than removed: container sharing is disabled for V1 and comes back with
   * teams, and their routes are still declared in the settings routing module (POST-010).
   *
   * "Workspaces" (route id `spaces`) is deliberately not listed, as in the Settings window: its routes
   * and backend stay live, so `/settings/spaces` is reachable only by typing the URL. The sibling
   * `Teams` row is absent for the same reason.
   */
  private static ALL_ITEMS: SidebarItem[] = [
    { title: 'General', icon: 'sapphire-contact', id: 'general' },
    { title: 'Account', id: 'general/account', parentId: 'general' },
    { title: 'Time & Date', id: 'general/time', parentId: 'general' },
    { title: 'Usage', id: 'general/usage', parentId: 'general' },
    { title: 'Billing', id: 'general/billing', parentId: 'general' },
    { title: 'Payment Methods', id: 'general/payment-methods', parentId: 'general' },
    // { title: 'Notifications', id: 'general/notifications', parentId: 'general' },
    { title: 'Security', id: 'general/security', parentId: 'general' },

    {
      title: 'Mail App',
      icon: 'sapphire-inbox',
      id: 'mail',
      permissions: [{ action: 'read', subject: ViewSubjects.mailView }],
    },
    // { title: 'General', id: 'mail/general', parentId: 'mail' },
    // { title: 'IMAP/POP', id: 'mail/imap-pop', parentId: 'mail' },
    { title: 'Email Permissions', id: 'mail/permissions', parentId: 'mail' },
    { title: 'Import', id: 'mail/import', parentId: 'mail' },
    { title: 'Signatures', id: 'mail/signatures', parentId: 'mail' },
    { title: 'Auto Reply', id: 'mail/auto-reply', parentId: 'mail' },
    { title: 'Templates', id: 'mail/templates', parentId: 'mail' },
    // { title: 'Quick Reply Templates', id: 'mail/quick-reply', parentId: 'mail' },
    { title: 'Shared Messages', id: 'mail/shared/message', parentId: 'mail' },
    // { title: 'Shared Folders', id: 'mail/shared/message-folder', parentId: 'mail' },

    {
      title: 'Calendar App',
      icon: 'sapphire-calendars',
      id: 'calendar',
      permissions: [{ action: 'read', subject: ViewSubjects.calendarView }],
    },
    { title: 'Default', id: 'calendar/default', parentId: 'calendar' },
    // { title: 'Calendar', id: 'calendar/calendar', parentId: 'calendar' },
    { title: 'Shared Events', id: 'calendar/shared/event', parentId: 'calendar' },
    // { title: 'Shared Calendars', id: 'calendar/shared/calendar', parentId: 'calendar' },

    {
      title: 'Contacts App',
      icon: 'sapphire-contact',
      id: 'contacts',
      permissions: [{ action: 'read', subject: ViewSubjects.contactsView }],
    },
    { title: 'Shared Contacts', id: 'contacts/shared/contact', parentId: 'contacts' },
    // { title: 'Shared Groups', id: 'contacts/shared/group', parentId: 'contacts' },

    {
      title: 'Tasks App',
      icon: 'sapphire-tasks',
      id: 'tasking',
      permissions: [{ action: 'read', subject: ViewSubjects.taskingView }],
    },
    // { title: 'Default', id: 'tasking/default', parentId: 'tasking' },
    // { title: 'Start & Due Dates', id: 'tasking/due_dates', parentId: 'tasking' },
    { title: 'Shared Tasks', id: 'tasking/shared/task', parentId: 'tasking' },
    // { title: 'Shared Projects', id: 'tasking/shared/project', parentId: 'tasking' },

    {
      title: 'Notes App',
      icon: 'sapphire-notebook',
      id: 'notes',
      permissions: [{ action: 'read', subject: ViewSubjects.notesView }],
    },
    // { title: 'Default', id: 'notes/default', parentId: 'notes' },
    // { title: 'Additional Options', id: 'notes/additional', parentId: 'notes' },
    { title: 'Shared Notes', id: 'notes/shared/note', parentId: 'notes' },
    // { title: 'Shared Notebooks', id: 'notes/shared/notebook', parentId: 'notes' },

    {
      title: 'Sheets App',
      icon: 'sapphire-sheets',
      id: 'sheets',
      permissions: [{ action: 'read', subject: ViewSubjects.sheetsView }],
    },
    // Commented out alongside every other app's container row, which is how they all ship.
    { title: 'Shared Workbooks', id: 'sheets/shared/sheet', parentId: 'sheets' },
    // { title: 'Shared Workbook Folders', id: 'sheets/shared/sheet-folder', parentId: 'sheets' },

    {
      title: 'Files App',
      icon: 'sapphire-file',
      id: 'files',
      permissions: [{ action: 'read', subject: ViewSubjects.filesView }],
    },
    { title: 'Shared Files', id: 'files/shared/file', parentId: 'files' },
    // { title: 'Shared Folders', id: 'files/shared/folder', parentId: 'files' },
  ];

  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly permissionsService: PermissionsService<Actions, Subjects> = inject(PermissionsService);

  protected readonly currentItem = signal<Pick<SidebarItem, 'id' | 'parentId'> | undefined>(undefined);

  protected readonly items = computed<SidebarItem[]>(() => {
    if (!this.permissionsService.hasPermissionsLoaded()) {
      return [];
    }
    return SettingsSideBarComponent.ALL_ITEMS.filter(
      (item) =>
        !item.permissions ||
        item.permissions.every((permission) =>
          this.permissionsService.checkPermission(permission.action, permission.subject),
        ),
    );
  });

  public ngOnInit(): void {
    this.currentItem.set(this.urlToCurrentItem(this.router.url));

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event: NavigationEnd) => {
        this.currentItem.set(this.urlToCurrentItem(event.url));
      });
  }

  protected selectFilter(item: SidebarItem, expandable = 0, expanded?: Subject<boolean>): void {
    if (expandable) {
      // Expandable headers have no page of their own — expand and open their first child.
      expanded?.next(true);

      const firstChild = this.items().find((child) => child.parentId === item.id);
      if (firstChild) {
        this.router.navigate([`/settings/${firstChild.id}`]);
        return;
      }
    }

    this.router.navigate([`/settings/${item.id}`]);
  }

  protected isItemActive(item: SidebarItem): boolean {
    const currentItem = this.currentItem();
    return currentItem?.id === item.id || currentItem?.parentId === item.id;
  }

  private urlToCurrentItem(url: string): Pick<SidebarItem, 'id' | 'parentId'> {
    const pathElements = url.split('/').slice(2);

    return {
      id: pathElements.join('/'),
      parentId: pathElements[0],
    };
  }
}
